import { useCallback, useEffect, useState } from "react";


import { Button, Col, Container, Form, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import { useDispatch } from "react-redux";

import { useQuery } from "@tanstack/react-query";
import fr from "date-fns/locale/fr";
import { DateTime, Settings, WeekdayNumbers } from "luxon";

import { PropsFromReduxAgenda } from "../containers/AgendaContainer";
import AgendaTaskMaker from "./common/AgendaTaskMaker";
import ModalAgendaPreferences from "./common/ModalAgendaPreferences";
import { dateFormat } from "../../../_helpers/dateHelper.ts";
import { capitalizeWord } from "../../../_helpers/stringHelper";
import { listAgency } from "../../../_services/serviceService.ts";
import { defaultTheme } from "../../../assets/css/defaultTheme";
import Gear from "../../../assets/svg/Gear";
import SelectAgency from "../../../common/components/SelectAgency.tsx";
import { Permissions } from "../../../common/enums/sharedEnums.ts";
import { Agency } from "../../../common/interfaces.ts";
import { setAgendaRange } from "../actions";
import { AgendaRangeType } from "../enums";
import { AgendaRange } from "../reducers";

registerLocale("fr", fr);
setDefaultLocale("fr");

Settings.defaultLocale = "fr";

enum NavigationDirection {
  forward = "forward",
  backward = "backward",
}

const AgendaComponent = (props: PropsFromReduxAgenda) => {
  const todayDate = DateTime.now();
  const dispatch = useDispatch();

  // TODO LATER FEATURE TEAM MEMBER
  // const [checkedTeamMembers, setCheckedTeamMembers] = useState<ITeamMember[]>(
  //   [],
  // );
  const [tempDate, setTempDate] = useState(todayDate);
  const [agency, setAgency] = useState<{ label: string; value: string }>();
  const [selectedWeek, setSelectedWeek] = useState({
    hour: [] as DateTime[],
    week: [] as DateTime[],
    month: todayDate.toFormat("MMMM"),
    year: todayDate.toFormat("yyyy"),
  });
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);

  useEffect(() => {
    if (agency) {
      agencyHandler(agency);
    }
  }, [agency, tempDate, selectedWeek]);

  useEffect(() => {
    const hoursRange: DateTime[] = [];
    const { timeFrom, timeTo, range } = props.rangeData;

    let start = DateTime.now().set({
      hour: timeFrom.hour,
      minute: timeFrom.minute,
    });

    const end = DateTime.now().set({
      hour: timeTo.hour,
      minute: timeTo.minute,
    });

    while (start <= end) {
      hoursRange.push(start.startOf("minute"));
      start = start.plus({ minutes: range });
    }
    // by default the DateTime will set the day at today but in our case we don't use it
    setSelectedWeek({ ...selectedWeek, hour: hoursRange });

    reloadWeek();
  }, [tempDate, props.rangeData]); //eslint-disable-line

  const changeDate = (direction: NavigationDirection) => {
    const firstDayOfMonth = tempDate.startOf("month");
    const numberDaysInMonth = firstDayOfMonth.daysInMonth;
    switch (props.rangeData.type) {
      case AgendaRangeType.DAY:
        if (direction === NavigationDirection.forward) {
          setTempDate(tempDate.plus({ day: 1 }));
        } else {
          setTempDate(tempDate.minus({ day: 1 }));
        }
        break;
      case AgendaRangeType.MONTH:
        if (direction === NavigationDirection.forward) {
          setTempDate(firstDayOfMonth.plus({ days: numberDaysInMonth }));
        } else {
          setTempDate(firstDayOfMonth.minus({ days: numberDaysInMonth }));
        }
        break;
      case AgendaRangeType.WEEK:
      case AgendaRangeType.CUSTOM:
        if (direction === NavigationDirection.forward) {
          setTempDate(tempDate.plus({ week: 1 }));
        } else {
          setTempDate(tempDate.minus({ week: 1 }));
        }
        break;
    }
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  const getServicesByDateTime = (targetDateTime: DateTime) => {
    return Object.values(props.services).filter((service) => {

      if (service.appointment && service.appointment.startDate) {
        const serviceDateTime = DateTime.fromISO(service.appointment.startDate);

        const isTheClosestTime = () => {
          const max = targetDateTime.plus({ minutes: props.rangeData.range });
          return serviceDateTime >= targetDateTime && serviceDateTime <= max;
        };
        return (
          serviceDateTime.hasSame(targetDateTime, "day") &&
          serviceDateTime.hasSame(targetDateTime, "hour") &&
          //@todo handle minute better than this
          isTheClosestTime()
        );
      }
      return null;
    });
  };

  const reloadWeek = () => {
    const { type, dayFrom, dayTo } = props.rangeData;
    let targetedDate = tempDate;
    if (type !== AgendaRangeType.DAY) {
      targetedDate = targetedDate.startOf("week");
    }

    setSelectedWeek((selectedWeek) => ({
      ...selectedWeek,
      week: [],
      month: targetedDate.toFormat("MMMM"),
      year: targetedDate.toFormat("yyyy"),
    }));
    const firstDayOfMonth = tempDate.startOf("month");
    const numberDaysInMonth = firstDayOfMonth.daysInMonth;
    const newWeek: DateTime[] = [];
    const daysOfWeek: WeekdayNumbers = 7; // Number of days in a week
    switch (type) {
      case AgendaRangeType.DAY:
        setSelectedWeek((selectedWeek) => ({
          ...selectedWeek,
          week: [tempDate],
        }));
        break;
      case AgendaRangeType.MONTH:
        if (undefined !== numberDaysInMonth) {
          const monthWeeks: DateTime[] = [];
          for (let i = 0; i < numberDaysInMonth; i++) {
            monthWeeks.push(firstDayOfMonth.plus({ days: i }));
          }
          setSelectedWeek((selectedWeek) => ({
            ...selectedWeek,
            week: monthWeeks,
          }));
        }
        break;
      case AgendaRangeType.WEEK:
      case AgendaRangeType.CUSTOM:
        if (dayFrom === 1 && dayTo === 7) {
          for (let i = 0; i < 7; i++) {
            newWeek.push(targetedDate.plus({ days: i }));
          }
        } else {
          let currentDay = dayFrom;
          while (currentDay !== (dayTo + 1) % (daysOfWeek + 1)) {
            newWeek.push(targetedDate.plus({ days: currentDay - 1 }));
            currentDay = ((currentDay % daysOfWeek) + 1) as WeekdayNumbers;
          }
        }
        setSelectedWeek((selectedWeek) => ({
          ...selectedWeek,
          week: newWeek,
        }));
        break;
    }
  };

  const selectDay = (newDate: DateTime) => {
    setTempDate(newDate);
    setSelectedWeek({ ...selectedWeek, month: newDate.toFormat("MMMM") });
    updateAgendaRange({ ...props.rangeData, type: AgendaRangeType.DAY });
  };

  const updateAgendaRange = (rangeData: AgendaRange) => {
    dispatch(setAgendaRange(rangeData));
  };

  const agencyHandler = useCallback(
    (agency: { value: string; label: string }) => {
      const { week } = selectedWeek;
      if (week.length < 1) {
        return;
      }
      props.getServicesAgendaTeam(
        week[0].startOf("day").toFormat(dateFormat),
        week[week.length - 1].endOf("day").toFormat(dateFormat),
        agency.value
      );
    },
    [selectedWeek]
  );

  const {
    data: agencies,
    isLoading: loadingAgencies,
    error: errorAgencies,
  } = useQuery<Agency[], Error>(
    ["listAgency", Permissions.BUILDING_DWELLING_CREATE],
    () => listAgency(Permissions.BUILDING_DWELLING_CREATE)
  );

  return (
    <div className="bg-container">
      {/*Agenda top-bar*/}
      <div className="d-flex flex-row flex-wrap gap-3 justify-content-between mb-3">
        <Row className="my-auto align-items-center text-center">
          <Col md={2} xs={12}>
            <div className="d-flex justify-content-center ms-4">
              <p
                className="m-0 fw-bolder"
                style={{ color: defaultTheme.palette.primary.light }}
              >
                {capitalizeWord(selectedWeek.month)}
              </p>
              <p className="m-0">&nbsp;</p>
              <p
                className="m-0"
                style={{ color: defaultTheme.palette.primary.light }}
              >
                {selectedWeek.year}
              </p>
            </div>
          </Col>
          <Col md={5} xs={12}>
            <div className="d-flex justify-content-center">
              <Button
                variant="none"
                onClick={() => changeDate(NavigationDirection.backward)}
                style={{ color: defaultTheme.palette.primary.main }}
              >
                <i className="bi bi-chevron-left m-0 p-0" />
              </Button>
              <Button
                variant="none"
                className="fw-bolder"
                onClick={() => setTempDate(todayDate)}
                style={{ color: defaultTheme.palette.primary.main }}
              >
                Aujourd'hui
              </Button>
              <Button
                variant="none"
                onClick={() => changeDate(NavigationDirection.forward)}
                style={{ color: defaultTheme.palette.primary.main }}
              >
                <i className="bi bi-chevron-right m-0 p-0" />
              </Button>
            </div>
          </Col>
          <Col md={3} xs={12}>
            <DatePicker
              className="date-select-bis add-prop-input-bis agenda-popover"
              dateFormat="dd/MM/yyyy"
              onChange={(date) =>
                date ? selectDay(DateTime.fromJSDate(date)) : null
              }
              placeholderText="Aller à une date"
              formatWeekDay={(nameOfDay) =>
                nameOfDay.toString().toUpperCase().substr(0, 1)
              }
            />
          </Col>
        </Row>
        <div className="d-flex align-items-center gap-3">
          <label className="add-prop-label ms-2">Agence :</label>
          <div className="select-creatable-team-member-form">
            <SelectAgency
              loading={loadingAgencies}
              error={errorAgencies}
              agencies={agencies}
              rightValue={Permissions.AGENDA_GET}
              setAgency={setAgency}
            />
          </div>
        </div>
        <div className="d-flex gap-3 align-items-center">
          <Form.Select
            className="pb-1 form-prop-input text-muted"
            style={{ cursor: "pointer" }}
            value={props.rangeData.type}
            onChange={(e) => {
              updateAgendaRange({
                ...props.rangeData,
                type: e.target.value as AgendaRangeType,
              });
            }}
          >
            {/*<option value={AgendaRangeType.DAY}>Jour</option>*/}
            <option value={AgendaRangeType.WEEK}>Semaine</option>
            <option value={AgendaRangeType.MONTH}>Mois</option>
            <option value={AgendaRangeType.CUSTOM} disabled>
              Personnalisé
            </option>
          </Form.Select>
          {/* TODO LATER FEATURE TEAM MEMBER */}
          {/*<AgendaTeamComponent*/}
          {/*  team={props.teamMembers}*/}
          {/*  user={props.user as any}*/}
          {/*/>*/}
          <Button variant="none" onClick={() => setShowPreferencesModal(true)}>
            <Gear color={defaultTheme.palette.primary.main} />
          </Button>
        </div>
      </div>
      <Container fluid>
        <Row className="flex-nowrap overflow-auto">
          <Col className="text-center mt-4 col-sm-auto">
            {selectedWeek.hour.map((time, i) => {
              return (
                <div
                  key={time.toLocaleString(DateTime.TIME_24_SIMPLE) + i}
                  className="hour mt-n2"
                >
                  {time.toLocaleString(DateTime.TIME_24_SIMPLE)}
                </div>
              );
            })}
          </Col>
          {selectedWeek.week.map((day, i) => {
            return (
              <Col
                key={day.toLocaleString()}
                className={
                  i < getDaysInMonth(tempDate.year, tempDate.month)
                    ? "border-end text-center"
                    : "text-center"
                }
              >
                <div className="d-flex d-flex-row justify-content-center gap-1">
                  <div
                    style={{
                      color:
                        day.day === todayDate.day &&
                        day.month === todayDate.month &&
                        day.year === todayDate.year
                          ? defaultTheme.palette.primary.light
                          : "inherit",
                    }}
                  >
                    <p className="mb-0 fw-bold">
                      {capitalizeWord(day.toFormat("EEEE"))}
                    </p>
                  </div>
                  <div
                    style={{
                      color:
                        day.day === todayDate.day &&
                        day.month === todayDate.month &&
                        day.year === todayDate.year
                          ? defaultTheme.palette.primary.light
                          : "inherit",
                    }}
                  >
                    <p className="mb-0 fw-bold">{day.day}</p>
                  </div>
                </div>
                <Row>
                  {selectedWeek.hour.map((time, i) => {
                    const dateTime = day.set({
                      hour: time.hour,
                      minute: time.minute,
                    });
                    return (
                      <div
                        key={dateTime.toLocaleString(DateTime.DATETIME_SHORT)}
                        className={
                          i === 0
                            ? "day border-top mt-2 p-0"
                            : "day border-top p-0"
                        }
                      >
                        <AgendaTaskMaker
                          cellDateTime={dateTime}
                          range={props.rangeData.range}
                          team={props.teamMembers}
                          services={getServicesByDateTime(dateTime)}
                        />
                      </div>
                    );
                  })}
                </Row>
              </Col>
            );
          })}
        </Row>
      </Container>

      <ModalAgendaPreferences
        team={props.teamMembers}
        rangeData={props.rangeData}
        show={showPreferencesModal}
        setShow={setShowPreferencesModal}
      />
    </div>
  );
};

export default AgendaComponent;
